import { type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { capitalize, messageFromError, type CompanyId } from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Group, Select } from '@mantine/core'
import { mergeGetHookResponses, usePermissionCheck, useProfile, useTranslation } from '@front/model'
import { Page, NotReady, TAlert, TButton } from '@components'
import { VisibilityModal } from './VisibilityModal'
import { isPermissionOption, parseAsPermissionOption, PERMISSION_OPTIONS, type PermissionOption } from './util'
import { UsersPermissions } from './UsersPermissions'
import { TeamPermissions } from './TeamPermissions'
import { RolePermissions } from './RolePermissions'
import { CompanyPermissions } from './CompanyPermissions'

export function Permissions(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const profileState = useProfile()
  const { company } = profileState
  const [viewOption, setViewOption] = useQueryState<PermissionOption>(
    'viewOption',
    parseAsPermissionOption.withDefault('company')
  )

  const permissionState = usePermissionCheck({
    permissionType: 'user:manage',
    targetId: company?.companyId,
  })
  const state = mergeGetHookResponses([profileState, permissionState])

  if (state.isLoading || state.error || !company)
    return <NotReady error={messageFromError(state.error)} isLoading={state.isLoading} />

  return (
    <Page>
      <Page.Heading title='Permissions'>
        <VisibilityModal companyId={company.companyId} />
        <TButton
          icon='compare_arrows'
          variant='secondary'
          onClick={() => navigate({ to: '/settings/permissions/compare' })}
        >
          {t('Compare')}
        </TButton>
      </Page.Heading>
      <Page.FunctionBar>
        <Group flex={1}>
          <Select
            allowDeselect={false}
            clearable={false}
            data={PERMISSION_OPTIONS.map((option) => ({ value: option, label: capitalize(option) }))}
            disabled={!permissionState.data}
            flex={1}
            label='View permissions for'
            value={viewOption}
            onChange={(value) => setViewOption(isPermissionOption(value) ? value : null)}
          />
        </Group>
      </Page.FunctionBar>
      {viewOption === 'team' ? (
        <Page.Content noPadding>
          {permissionState.data ? (
            <PageContent companyId={company.companyId} viewing={viewOption} />
          ) : (
            <TAlert>You do not have permission to manage permissions for {company.name}</TAlert>
          )}
        </Page.Content>
      ) : (
        <Page.Content scrollArea>
          {permissionState.data ? (
            <PageContent companyId={company.companyId} viewing={viewOption} />
          ) : (
            <TAlert>You do not have permission to manage permissions for {company.name}</TAlert>
          )}
        </Page.Content>
      )}
    </Page>
  )
}

type PageContentProps = {
  viewing: PermissionOption
  companyId: CompanyId
}

const PageContent = ({ viewing, companyId }: PageContentProps): ReactElement => {
  switch (viewing) {
    case 'team':
      return <TeamPermissions />
    case 'user':
      return <UsersPermissions />
    case 'company':
      return <CompanyPermissions companyId={companyId} />
    case 'role':
      return <RolePermissions />
  }
}

export const Route = createFileRoute('/settings/permissions/')({
  component: Permissions,
})
